/**
 * Shared blob/file download helpers for browser-side downloads triggered by
 * authenticated XHR responses (CSV exports, mention media, signed PDFs, etc).
 */

/**
 * Parses a `filename` value out of a Content-Disposition header. Tries the
 * RFC 5987 UTF-8 form first, then falls back to the legacy quoted ASCII form.
 */
export function parseFilenameFromDisposition(header: string | undefined): string | undefined {
  if (!header) return undefined;

  const utf8Match = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // Fall through to the ASCII filename variant.
    }
  }

  const asciiMatch = header.match(/filename\s*=\s*"?([^";]+)"?/i);
  return asciiMatch?.[1];
}

/**
 * Triggers a browser download by creating a hidden anchor element and
 * clicking it. The blob URL is revoked on the next macrotask so the browser
 * has time to start the download.
 */
export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/**
 * Decodes a JSON envelope returned inside a Blob (used when an endpoint can
 * return either a binary file or a JSON status response and the caller
 * requested `responseType: 'blob'` for the binary path).
 */
export async function parseJsonFromBlob<T>(blob: Blob): Promise<T> {
  const text = await blob.text();
  return JSON.parse(text) as T;
}
